import { Active, Over } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import cloneDeep from "lodash/cloneDeep";

import i18n from "../../i18n";
import { KanbanColumn } from "../../model/kanban/KanbanColumn";
import { KanbanTemplates } from "../../model/kanban/types/KanbanTemplates.enum";
import camelCase from "lodash/camelCase";

type GenericReturn = {
  newOrder: number;
  oldOrder: number;
  newColumns: KanbanColumn[];
};

export const getNewKanbanStateForColumnsDND = (data: {
  columns: KanbanColumn[];
  activeCol: KanbanColumn;
  overCol: KanbanColumn;
}): GenericReturn => {
  const { overCol, activeCol, columns } = data;

  const oldOrder = columns.findIndex((col) => col._id === activeCol._id);
  let newOrder = columns.findIndex((el) => el._id === overCol._id);
  newOrder = newOrder === -1 ? 0 : newOrder;

  const newColumns = arrayMove(columns, oldOrder, newOrder);

  return { newOrder, oldOrder, newColumns };
};

export const getNewColumnsStateForSameColumnDND = (data: {
  currentColumn: KanbanColumn;
  columns: KanbanColumn[];
  active: Active;
  over: Over;
}): GenericReturn => {
  const { currentColumn, over, active, columns } = data;

  const targetCol = cloneDeep(
    columns.find((col) => col._id === currentColumn._id)!,
  );

  const oldOrder = targetCol.cards!.findIndex((el) => el._id === active.id);
  let newOrder = targetCol.cards!.findIndex((el) => el._id === over.id);
  // If the new order is -1 (empty column), set it to 0
  newOrder = newOrder === -1 ? 0 : newOrder;
  targetCol.cards = arrayMove(targetCol.cards!, oldOrder, newOrder);

  const newColumns = columns.map((_col) =>
    _col._id === targetCol._id ? targetCol : _col,
  );

  return { newOrder, oldOrder, newColumns };
};

export const getNewColumnsStateForDifferentColumnsDND = (data: {
  currentColumn: KanbanColumn;
  sourceColumn: KanbanColumn;
  columns: KanbanColumn[];
  active: Active;
  over: Over;
}): GenericReturn => {
  const { currentColumn, sourceColumn, active, over, columns } = data;

  const targetCol = cloneDeep(
    columns.find((col) => col._id === currentColumn._id)!,
  );

  const sourceCol = cloneDeep(
    columns.find((col) => col._id === sourceColumn._id)!,
  );

  const oldOrder = sourceCol.cards!.findIndex((el) => el._id === active.id);
  let newOrder = targetCol.cards!.findIndex((el) => el._id === over.id);
  // If the new order is -1 (empty column), set it to 0
  newOrder = newOrder === -1 ? 0 : newOrder;

  // Remove card from source column and insert into target column
  const [movedCard] = sourceCol.cards!.splice(oldOrder, 1);
  targetCol.cards!.splice(newOrder, 0, movedCard);

  const newColumns = columns.map((_col) => {
    if (_col._id === targetCol._id) {
      return targetCol;
    } else if (_col._id === sourceCol._id) {
      return sourceCol;
    } else {
      return _col;
    }
  });

  return { newOrder, oldOrder, newColumns };
};

const stepRegex = /^Step \d+$/i;

export const getTranslatedKanbanName = (name: string) => {
  if (name === "default-pipeline") {
    return i18n.t("kanban.name.default");
  }

  return name;
};

const templateHasColumn = (template: KanbanTemplates, columnName: string) =>
  (kanbanTemplatseColumns[template] || []).some(
    (c) => camelCase(c) === camelCase(columnName),
  );

/**
 * The template that actually defines this column name.
 *
 * A pipeline keeps its own template while its columns can be renamed to
 * anything, so a column called "Ideas" (a Marketing default) can sit in a
 * Default pipeline. Keying the lookup off the *pipeline's* template then asks
 * for `kanban.templates.default.columns.ideas`, which does not exist.
 */
const findTemplateForColumnName = (columnName: string) =>
  (Object.keys(kanbanTemplatseColumns) as KanbanTemplates[]).find((template) =>
    templateHasColumn(template, columnName),
  );

const isDefaultColumnName = (columnName: string) => {
  const trimmedName = columnName.trim();

  return (
    Boolean(findTemplateForColumnName(trimmedName)) ||
    stepRegex.test(trimmedName)
  );
};

export const getTranslatedKanbanColumnName = (
  name?: string,
  template?: KanbanTemplates,
) => {
  const trimmedName = name?.trim() || "";

  if (!trimmedName) {
    return name;
  }

  if (stepRegex.test(trimmedName)) {
    return i18n.t("kanban.column.title.step", {
      step: trimmedName.match?.(/\d+/)?.[0],
    });
  }

  if (!isDefaultColumnName(trimmedName)) {
    return name;
  }

  // Prefer the pipeline's own template, but only when it really defines this
  // column; otherwise fall back to whichever template does.
  const owningTemplate =
    template && templateHasColumn(template, trimmedName)
      ? template
      : findTemplateForColumnName(trimmedName);

  // `defaultValue` keeps the raw column name on screen if a key is ever missing,
  // rather than printing the lookup itself.
  return owningTemplate
    ? i18n.t(
        `kanban.templates.${camelCase(owningTemplate)}.columns.${camelCase(trimmedName)}`,
        { defaultValue: name },
      )
    : i18n.t(`kanban.column.title.${camelCase(trimmedName)}`, {
        defaultValue: name,
      });
};

export const kanbanTemplatseColumns = {
  [KanbanTemplates.DEFAULT]: [
    "Step 1",
    "Step 2",
    "Step 3",
    "Closed Won",
    "Closed Lost",
    "Closed To Competition",
  ],
  [KanbanTemplates.SALES]: [
    "New Deals",
    "Qualifications",
    "Proposals",
    "Review",
    "Closed Won",
    "Closed Lost",
    "Closed To Competition",
  ],
  [KanbanTemplates.CUSTOMER_SUPPORT]: [
    "New Tickets",
    "Qualifications",
    "In Progress",
    "On Hold",
    "Closed Won",
    "Closed Lost",
    "Closed To Competition",
  ],
  [KanbanTemplates.MARKETING]: [
    "Ideas",
    "Planning",
    "Execution",
    "Analysis",
    "Closed Won",
    "Closed Lost",
    "Closed To Competition",
  ],
};
